// MainLayout.tsx
import React from 'react'
import {Layout} from 'antd'
import classNames from 'classnames'
import {observer} from 'mobx-react'
import {IdleTimer} from 'dna-common'
import {useInjection} from 'dna-react-ioc'

import {useLayout} from '@/hooks'
import {IDLE_TIME_OUT_TIME} from '@/constants'
import {UserGuideContainer, AIChatMessenger} from '@/components'
import {LayoutContent} from '@/layouts/LayoutContent'
import {Sider} from '@/layouts/MainLayout/components'
import {SENDBIRD_CONFIG} from '@/constants/sendbird'
import {isMobileAppRedirectVisible} from '@/constants/mobile-app-redirect'

import {IMainLayoutStore} from './IMainLayoutStore'
import {MainLayoutProps} from './props'
import styles from './styles.scss'

export const MainLayoutStoreSymbol = Symbol('mainLayoutStore')
export const NotificationStoreSymbol = Symbol('NotificationStoreSymbol')
export const AuthNotificationStoreSymbol = Symbol('AuthNotificationStoreSymbol')

export const MainLayout = observer(({children, theme}: MainLayoutProps) => {
    const store = useInjection<IMainLayoutStore>(MainLayoutStoreSymbol)
    const {merchant, menuItems, merchantCompanyInfo, setOpenKeys} = store

    const layoutProps = useLayout(store, menuItems)

    const {
        isMobile,
        isCollapsed,
        selectedKey,
        openKeys,
        setCollapsed,
        onOpenChange,
        onCancelSession
    } = layoutProps

    return (
        <Layout className={classNames(styles.layout, {mobile: isMobile})}>
            <IdleTimer onCancelSession={onCancelSession} timeOut={IDLE_TIME_OUT_TIME} />
            {/* Only create one instance of SendbirdMessenger based on environment */}
            {merchant?.merchantId &&
                merchantCompanyInfo &&
                (_IS_PROD_ || _IS_TEST_) &&
                SENDBIRD_CONFIG.IS_VISIBLE && <AIChatMessenger />}
            <Sider
                isMobile={isMobile}
                isCollapsed={isCollapsed}
                setCollapsed={setCollapsed}
                menuItems={menuItems}
                openKeys={isCollapsed ? [] : openKeys}
                selectedKeys={selectedKey ? [selectedKey] : []}
                onOpenChange={onOpenChange}
            />

            <LayoutContent
                {...layoutProps}
                theme={theme}
                showMobileAppRedirect={isMobileAppRedirectVisible}
            >
                {children}
            </LayoutContent>

            <UserGuideContainer />
        </Layout>
    )
})
